import { apiFetch } from "./api";
import type {
  CreateVoiceNoteRequest,
  VoiceNoteUploadUrlRequest,
  VoiceNoteUploadUrlResponse,
} from "@family/shared";

/** The first recording format this browser supports. Chrome and Firefox
 *  record Opus in WebM; Safari only records AAC in MP4. */
export function pickRecordingMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return candidates.find((m) => MediaRecorder.isTypeSupported(m));
}

export function canRecordAudio(): boolean {
  return (
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== "undefined"
  );
}

/** Upload a recording and attach it to a photo. */
export async function uploadVoiceNote(
  familySlug: string,
  mediaId: string,
  blob: Blob,
  durationMs: number,
): Promise<void> {
  const req: VoiceNoteUploadUrlRequest = {
    mimeType: blob.type || "audio/webm",
    fileSize: blob.size,
  };
  const signed = await apiFetch<VoiceNoteUploadUrlResponse>(
    `/families/${familySlug}/media/${mediaId}/voice-notes/upload-url`,
    { method: "POST", body: JSON.stringify(req) },
  );

  const put = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": signed.mimeType },
    body: blob,
  });
  if (!put.ok) throw new Error(`Voice note upload failed: ${put.status}`);

  const body: CreateVoiceNoteRequest = {
    voiceNoteId: signed.voiceNoteId,
    mimeType: signed.mimeType,
    durationMs: Math.round(durationMs),
  };
  await apiFetch(`/families/${familySlug}/media/${mediaId}/voice-notes`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  // Speech-to-text runs server-side and can take a while; the scan is saved
  // already, so this is not awaited and a failure is only logged. The album
  // offers a retry for anything that didn't get transcribed.
  void apiFetch(
    `/families/${familySlug}/media/${mediaId}/voice-notes/${signed.voiceNoteId}/transcribe`,
    { method: "POST" },
  ).catch((err) => console.warn("voice note transcription failed", err));
}
